import { useState, useCallback } from 'react';
import { LogOut, GraduationCap, Settings, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { useTutorial } from '../../contexts/TutorialContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import Dropdown from '../ui/Dropdown';

export default function UserProfileMenu() {
  const { user, signOut, getAuthHeaders } = useUser();
  const { startTutorial } = useTutorial();
  const { isAdmin } = useTheme();
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const onClose = useCallback(() => setIsOpen(false), []);

  if (!user) return null;

  const getInitials = () => {
    if (user.displayName) {
      return user.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (user.email) {
      return user.email[0].toUpperCase();
    }
    return '?';
  };

  return (
    <Dropdown
      isOpen={isOpen}
      onClose={onClose}
      width="w-64"
      trigger={
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative w-9 h-9 rounded-full bg-card flex items-center justify-center text-foreground font-medium text-sm hover:ring-2 hover:ring-foreground/30 transition-all overflow-hidden border border-border"
        >
          {user.photoURL && (
            <img
              src={user.photoURL}
              alt={user.displayName || 'User'}
              className="w-full h-full object-cover absolute inset-0"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          <span className="text-foreground font-medium">{getInitials()}</span>
        </button>
      }
    >
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-3">
          {user.photoURL ? (
            <img
              src={user.photoURL}
              alt={user.displayName || 'User'}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-medium">
              {getInitials()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-foreground truncate">{user.displayName}</p>
            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
          </div>
        </div>
      </div>
      <div className="p-2 space-y-1">
        <button
          onClick={() => {
            setIsOpen(false);
            startTutorial();
          }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-background rounded-lg transition-colors"
        >
          <GraduationCap className="w-4 h-4" />
          {t('header.tutorial')}
        </button>
        <button
          onClick={async () => {
            if (resetting) return;
            setResetting(true);
            try {
              const headers = await getAuthHeaders();
              const res = await fetch('/api/orders/reset', {
                method: 'POST',
                headers
              });
              const data = await res.json();
              if (data.success) {
                alert(`Account gereset! ${data.created} demo bestellingen aangemaakt.`);
                window.location.reload();
              }
            } catch {
              alert('Reset mislukt');
            } finally {
              setResetting(false);
              setIsOpen(false);
            }
          }}
          disabled={resetting}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-warning hover:text-warning hover:bg-warning/10 rounded-lg transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${resetting ? 'animate-spin' : ''}`} />
          {resetting ? t('general.loading') : t('header.reset_demo')}
        </button>
        <Link
          to="/admin"
          onClick={() => setIsOpen(false)}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-background rounded-lg transition-colors"
        >
          <Settings className="w-4 h-4" />
          {isAdmin ? t('nav.admin') : t('nav.memory')}
        </Link>
        <button
          onClick={() => {
            setIsOpen(false);
            signOut();
          }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-background rounded-lg transition-colors"
        >
          <LogOut className="w-4 h-4" />
          {t('header.logout')}
        </button>
      </div>
    </Dropdown>
  );
}
